import { StatusBadge } from '@/components/badge/badge.component';
import { SecondaryButton } from '@/components/buttons/button.component';
import { DataTable } from '@/components/ui/data-table';
import { FilterConfig } from '@/components/ui/filter';
import { useTableUrlState } from '@/hooks/useTableUrlState';
import { cn } from '@/lib/utils';
import { Project, useProjectList } from '@/modules/Customers/Customers.api';
import { usePermissions, UserPermissions } from '@/modules/Permissions';
import { ProjectStatus } from '@/modules/Projects/Projects.constants';
import { ColumnDef, ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import clsx from 'clsx';
import { Pencil } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export const ProjectsTable = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const canEditProject = hasPermission(UserPermissions.EDIT_PROJECT);

  const columns: ColumnDef<Project, unknown>[] = [
    {
      header: 'Project Name',
      accessorKey: 'project_name',
      meta: { className: 'w-1/8' },
    },
    {
      header: 'Project ID',
      accessorKey: 'project_id',
      meta: { className: 'w-1/8' },
    },
    {
      header: 'Sampling Event',
      accessorKey: 'sampling_event',
      cell: ({ row }) => {
        const samplingEvent = row.original.sampling_event;
        return (
          <div className={cn('flex justify-center')}>
            <span>{samplingEvent !== null && samplingEvent !== undefined ? samplingEvent : '-'}</span>
          </div>
        );
      },
      meta: { className: 'w-1/8' },
    },
    {
      header: 'PM Name',
      accessorFn: (project) => project.owner?.name || '-',
      enableSorting: false, // Sorting on calculated fields not currently supported
      meta: { className: 'w-1/8' },
    },
    {
      header: 'Phone',
      accessorFn: (project) => project.owner?.phone_number || '-',
      enableSorting: false, // Sorting on calculated fields not currently supported
      meta: { className: 'w-1/8' },
      cell: ({ row }) => {
        const phone = row.original.owner?.phone_number || '-';
        return (
          <span title={phone} className="block max-w-[140px] truncate">
            {phone}
          </span>
        );
      },
    },
    {
      header: 'Email',
      accessorFn: (project) => project.owner?.email || '-',
      enableSorting: false, // Sorting on calculated fields not currently supported
      cell: ({ row }) => {
        const email = row.original.owner?.email || '-';
        return (
          <span title={email} className="block max-w-[140px] truncate">
            {email}
          </span>
        );
      },
      meta: { className: 'w-1/8' },
    },
    {
      header: 'Farm Reports',
      accessorKey: 'status',
      enableSorting: false, // Sorting on calculated fields not currently supported
      cell: ({ row }) => {
        if (row.original.reporting_type === 'project_only') {
          return (
            <div className={cn('flex justify-center')}>
              <StatusBadge status="Not Available" />
            </div>
          );
        }
        const status = row.getValue<ProjectStatus>('status');
        return (
          <div className={cn('flex justify-center')}>
            <StatusBadge status={status} />
          </div>
        );
      },
      meta: { className: 'w-1/8' },
    },
    {
      header: 'Project Report',
      accessorKey: 'reporting_enabled',
      enableSorting: false,
      cell: ({ row }) => {
        const { reporting_enabled, status } = row.original;
        const navigateToReport = () => navigate(`/customers/${row.original.breadcrumb.client.id}/projects/${row.original.project_id}`);

        if (!reporting_enabled) {
          return (
            <div className="flex justify-center">
              <StatusBadge status="Not Available" />
            </div>
          );
        }

        const reportReady = status === 'Complete';
        const canViewReport = reportReady || hasPermission(UserPermissions.PROJECT_DETAIL);

        return (
          <div className="w-full flex justify-center">
            <div className="inline-flex">
              <SecondaryButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (canViewReport) navigateToReport();
                }}
                variant="stretch"
                size="small"
                disabled={!canViewReport}
                className={clsx(
                  'min-w-[85px] h-[32px] rounded-lg px-3 py-1.5 text-xs font-medium transition', // Adjusted padding and height
                  reportReady
                    ? 'bg-brand text-white hover:bg-brand-700'
                    : 'bg-white border border-neutral-200 text-neutral-400 hover:bg-neutral-100'
                )}
              >
                View Report
              </SecondaryButton>
            </div>
          </div>
        );
      },
      meta: { className: 'w-1/8' },
    },
    ...(canEditProject
      ? [
          {
            id: 'edit_project',
            header: 'Edit Project',
            cell: ({ row }: { row: { original: Project } }) => {
              const navigateToEditProject = () => navigate(`/projects/edit/${row.original.project_id}`);
              return (
                <div className="flex justify-center">
                  <SecondaryButton
                    type="button"
                    variant="fitted"
                    size="small"
                    className="bg-white border border-neutral-200 text-neutral-900 
                            rounded-lg px-3 py-1 text-sm font-medium
                            hover:bg-neutral-100 transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToEditProject();
                    }}
                  >
                    <Pencil />
                  </SecondaryButton>
                </div>
              );
            },
            meta: { className: 'w-1/8' },
          } as ColumnDef<Project, unknown>,
        ]
      : []),
  ];

  const { pagination, setPagination, sorting, setSorting, columnFilters, setColumnFilters } = useTableUrlState();

  const { customerId } = useParams<{ customerId: string }>();
  const { data, status } = useProjectList(pagination, sorting, columnFilters, customerId);

  if (status === 'error') {
    toast.error('Failed to fetch projects');
    return;
  }

  const handleRowClick = (project: Project) => {
    navigate(`/customers/${project.breadcrumb.client.id}/projects/${project.project_id}/farms/`);
  };

  const filters: FilterConfig[] = [
    {
      id: 'status',
      label: 'Reports Released',
      options: [
        {
          label: 'Incomplete',
          id: 'Incomplete',
        },
        {
          label: 'Partial',
          id: 'Partial',
        },
        {
          label: 'Complete',
          id: 'Complete',
        },
      ],
    },
  ];

  return (
    <div className={cn('p-5 flex-col gap-3')}>
      <DataTable
        columns={columns}
        data={data?.results}
        totalCount={data?.count}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
        showSearch={true}
        searchPlaceholder="Search Project Name or ID..."
        filters={filters}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        isLoading={status === 'pending'}
        tableRowClickable={(project) => project.reporting_type !== 'project_only'}
        onTableRowClick={handleRowClick}
      />
    </div>
  );
};
